import { userIsLegacyDefaultOrganization } from "../utils/legacyDefaultOrganization";

const ROLES = {
  ALL: ["Admin", "Sales Director", "Manager", "Sales Rep"],
  LEADERSHIP: ["Admin", "Sales Director", "Manager"],
  SETTINGS_FULL: ["Admin", "Sales Director"],
  ADMIN_ONLY: ["Admin"],
};

/** Sidebar-aligned page overview scripts (permission-gated). */
const NARRATION_ENTRIES = [
  {
    route: "/dashboard",
    roles: ROLES.ALL,
    script:
      "This is your home base. Check lead totals, performance, and recent activity. Start here daily, then open anything that needs action.",
  },
  {
    route: "/sales-pipeline",
    roles: ROLES.ALL,
    script:
      "Track proposals—sent, pending, and closed won. See monthly pipeline health and deals waiting on the customer.",
  },
  {
    route: "/follow-up-intelligence",
    roles: ROLES.LEADERSHIP,
    script:
      "Manager view of follow-up risk—overdue, upcoming, at-risk, and system activity—in lanes so you prioritize who to contact first.",
  },
  {
    route: "/territories",
    roles: ROLES.LEADERSHIP,
    script:
      "Territories are your markets and teams. Set up coverage, assign managers and reps, and put leads in the right territory.",
  },
  {
    route: "/leads",
    roles: ROLES.ALL,
    script:
      "Your main lead workspace—map or list. Update status, add notes, open details, and build routes. Most daily work happens here.",
  },
  {
    route: "/leads/search",
    roles: ROLES.ALL,
    script:
      "Find leads by name, address, phone, or filters. Use this when you know who you need and want to jump to the record.",
  },
  {
    route: "/message-center",
    roles: ROLES.ALL,
    script:
      "Deprecated. Message Center is no longer in the sidebar. The page stays until nothing else opens it.",
  },
  {
    route: "/conversations",
    roles: ROLES.ALL,
    script:
      "Two-way texting with leads. Read and send SMS and keep customer conversations in the CRM.",
  },
  {
    route: "/team-chat",
    roles: ROLES.ALL,
    script:
      "Internal team chat. Coordinate with your team and respond when you’re mentioned.",
  },
  {
    route: "/rep-positions",
    roles: ROLES.LEADERSHIP,
    script:
      "A live map showing where reps are in the field. Helps managers see who's working and which areas the team is covering.",
  },
  {
    route: "/changelog",
    roles: ROLES.ALL,
    script:
      "Release notes and updates. Check here after upgrades to see what changed.",
  },
  {
    route: "/settings/reports",
    roles: ["Admin", "Sales Director", "Manager"],
    script:
      "Reports and exports for performance and activity—filter by date and scope for reviews and coaching.",
  },
  {
    route: "/settings/company-profile",
    roles: ROLES.SETTINGS_FULL,
    script:
      "Your company profile—name, branding, and business details used on proposals and customer-facing touchpoints.",
  },
  {
    route: "/settings/products-services",
    roles: ROLES.SETTINGS_FULL,
    script:
      "Your sellable catalog—products, services, and pricing reps use on proposals so quotes stay consistent.",
  },
  {
    route: "/settings/roles",
    roles: ROLES.SETTINGS_FULL,
    requiresRoleSettingsAccess: true,
    script:
      "How permissions and groups are organized. Review roles and what each level can access in the CRM.",
  },
  {
    route: "/users/edit",
    roles: ROLES.SETTINGS_FULL,
    script:
      "Edit a CRM user—update profile details, account toggles, territory access, role permissions, and optional FillQuick credentials. Save when finished.",
  },
  {
    route: "/users",
    roles: ROLES.SETTINGS_FULL,
    script:
      "Manage CRM users—invite people, assign roles and territories, and deactivate accounts when someone leaves.",
  },
  {
    route: "/settings/subscription",
    roles: ROLES.ADMIN_ONLY,
    script:
      "Billing and subscription for your organization. Review plan status and licensing as the account admin.",
  },
  {
    route: "/settings/integrations",
    roles: ROLES.ADMIN_ONLY,
    script:
      "Connect external tools—email, maps, and services that power SMS, routing, and automation in the CRM.",
  },
  {
    route: "/settings/action-log",
    roles: ROLES.SETTINGS_FULL,
    script:
      "Audit trail of important system changes. See who did what and when for support or accountability.",
  },
  {
    route: "/leads/detail",
    roles: ROLES.ALL,
    script:
      "The full record for one lead—contact info, property details, notes, follow-ups, and an activity timeline of everything that has happened.",
  },
  {
    route: "/territory/add/lead",
    roles: ROLES.ALL,
    script:
      "Manage this territory and add leads—edit the territory, bulk upload from a file, or add one lead at a time.",
  },
  {
    route: "/territories/territorydetail",
    roles: ROLES.LEADERSHIP,
    script:
      "Territory overview—review details and open tools to manage leads in this market.",
  },
  {
    route: "/leadmap/savedroutes",
    roles: ROLES.ALL,
    script:
      "Review saved routes, open one on the map, and manage stops. Use this after building routes from the leads map.",
  },
];

export function resolveGuidedTourRoute(pathname) {
  if (!pathname) return null;

  if (pathname.startsWith("/territory/add/lead")) {
    return "/territory/add/lead";
  }
  if (pathname.startsWith("/territories/territorydetail")) {
    return "/territories/territorydetail";
  }
  if (pathname.startsWith("/leadmap/savedroutes")) {
    return "/leadmap/savedroutes";
  }
  if (/^\/users\/[^/]+\/edit$/.test(pathname)) {
    return "/users/edit";
  }

  if (NARRATION_ENTRIES.some((e) => e.route === pathname)) {
    return pathname;
  }

  if (pathname.startsWith("/leads/") && pathname !== "/leads/search") {
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length === 2 && segments[0] === "leads") {
      return "/leads/detail";
    }
    return "/leads";
  }
  if (pathname.startsWith("/territories") || pathname.startsWith("/territory/")) {
    return "/territories";
  }
  if (pathname.startsWith("/users")) {
    return "/users";
  }
  if (pathname.startsWith("/settings/")) {
    const match = NARRATION_ENTRIES.find(
      (e) => e.route.startsWith("/settings/") && pathname.startsWith(e.route)
    );
    if (match) return match.route;
  }

  return null;
}

export function canAccessRoleSettings(user, role) {
  return (
    user?.is_superuser === true ||
    (role === "Admin" && userIsLegacyDefaultOrganization(user))
  );
}

export function getNarrationForRoute(pathname, { role, user }) {
  const routeKey = resolveGuidedTourRoute(pathname);
  if (!routeKey || !role) return null;

  const entry = NARRATION_ENTRIES.find((e) => e.route === routeKey);
  if (!entry) return null;
  if (!entry.roles.includes(role)) return null;
  if (entry.requiresRoleSettingsAccess && !canAccessRoleSettings(user, role)) {
    return null;
  }

  return entry.script;
}
